import React from 'react';
import { useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plug } from 'lucide-react';
import type { McpConnectionCreate } from '../../generated/api/types.gen.js';
import { zMcpConnectionCreate } from '../../generated/api/zod.gen.js';
import {
  createMcpConnectionMutation,
  listMcpConnectionsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, Modal, useToast } from '../../components/ui/index.js';
import { Form, SelectFormField, SubmitButton, TextFormField, useZodForm } from '../../components/form/index.js';

interface McpConnectionModalProps {
  open: boolean;
  onClose: () => void;
}

const TRANSPORT_OPTIONS = [
  {
    value: 'stdio',
    label: 'Local process (stdio)',
    description: 'Conflui starts the command and talks to it directly',
  },
  { value: 'http', label: 'HTTP (streamable)', description: 'A remote MCP server reached over HTTP' },
  { value: 'sse', label: 'Server-sent events', description: 'A remote MCP server that streams over SSE' },
];

const schema = zMcpConnectionCreate.pick({
  name: true,
  description: true,
  transport: true,
  command: true,
  serverUrl: true,
});

/** Add an MCP server whose tools agents can call. */
export const McpConnectionModal: React.FC<McpConnectionModalProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const create = useMutation(createMcpConnectionMutation());
  const form = useZodForm(schema, {
    defaultValues: { name: '', description: '', transport: 'stdio', command: '', serverUrl: '' },
  });
  const transport = useWatch({ control: form.control, name: 'transport' });

  const close = () => {
    form.reset();
    onClose();
  };

  const submit = async (
    values: Pick<McpConnectionCreate, 'name' | 'description' | 'transport' | 'command' | 'serverUrl'>,
  ) => {
    await create.mutateAsync({ body: values });
    await queryClient.invalidateQueries({ queryKey: listMcpConnectionsQueryKey() });
    notify('Connection added');
    close();
  };

  return (
    <Modal open={open} onClose={close} title="Add MCP connection" titleIcon={<Plug size={16} />}>
      <Form form={form} onSubmit={submit}>
        <TextFormField name="name" label="Name" placeholder="Brave Search" required />
        <TextFormField name="description" label="Description" placeholder="What does this connection provide?" />
        <SelectFormField name="transport" label="Transport" options={TRANSPORT_OPTIONS} required />
        {transport === 'stdio' ? (
          <TextFormField
            name="command"
            label="Command"
            placeholder="npx @modelcontextprotocol/server-brave-search"
            description="Run on the worker host when the connection starts."
          />
        ) : (
          <TextFormField name="serverUrl" label="Server address" type="url" placeholder="https://mcp.example.com/mcp" />
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancel
          </Button>
          <SubmitButton>Add connection</SubmitButton>
        </div>
      </Form>
    </Modal>
  );
};
